<template>
  <div>
    <div class="audio-merging">
      <h2>音频合并</h2>
      <div class="content-area">
        <div class="left-area">
          <div class="audio-list">
            <div v-for="(audio, index) in uploadedAudios" :key="index" class="audio-item">
                <span class="audio-name">{{ audio.name }}</span>
                <div class="audio-controls">
                    <button @click="moveAudioUp(index)" :disabled="index === 0" v-if="index !== 0">↑</button>
                    <button @click="moveAudioDown(index)" :disabled="index === uploadedAudios.length - 1" v-if="index !== uploadedAudios.length - 1">↓</button>
                    <button @click="removeAudio(index)">×</button>
                    <button @click="togglePlayPause(index)" class="play-pause-btn">
                      <i :class="{'fas fa-play': !isPlaying[index], 'fas fa-pause': isPlaying[index]}"></i>
                    </button>
                </div>
            </div>
          </div>
        </div>
        <div class="right-area">
          <div class="upload-audio" @click="triggerFileUpload" @drop.prevent="handleFileDrop" @dragover.prevent>
            <div class="upload-icon">+</div>
            <p>点击上传或拖拽音频</p>
            <small>支持mp3、wav、ogg格式，大小不超过100MB</small>
            <input type="file" ref="fileInput" @change="handleFileSelect" accept="audio/*" style="display: none;" multiple>
          </div>
          <div class="output-filename">
            <label>合并后文件名：</label>
            <input type="text" v-model="outputFilename" placeholder="输入文件名">
          </div>
          <button class="process-button" @click="startMerging" :disabled="!isMergingAllowed">开始合并</button>
        </div>
      </div>
      <p class="terms">
        仅对本人音频进行处理，您承诺您的素材内容为您本人所有或已获得合法授权。并遵守《音频处理功能使用规范》
      </p>
    </div>

    <div class="user-records">
      <h3>用户操作记录</h3>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>文件名</th>
              <th>上传时间</th>
              <th>处理状态</th>
              <th>合并后的文件名</th>
              <th>下载</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(record, index) in userRecords" :key="index">
              <td>{{ record.file_names }}</td>
              <td>{{ record.upload_time }}</td>
              <td>{{ record.file_status }}</td>
              <td>{{ record.outputFileName }}</td>
              <td><a href="#" @click.prevent="downloadFile(record)">下载</a></td>
              <td><button @click="deleteRecord(record)">删除</button></td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <audio ref="audioPlayer" @ended="audioEnded"></audio>
  </div>
</template>

<script>
import { getUpQiniuToken, saveAudioAdjustFileUrl, getUserRecords, deleteUserRecord, uploadToQiniu } from '@/api/upfile'
import { QINIU_BASE_URL } from '@/api/constants'

export default {
  name: 'AudioMerging',
  data () {
    return {
      uploadedAudios: [],
      outputFilename: '',
      userRecords: [],
      currentAudioIndex: 0,
      isPlaying: {},
      file_names: [],
      tableNames: [
        'audioMerge'
      ]
    }
  },
  computed: {
    isMergingAllowed () {
      return this.uploadedAudios.length > 1 && this.outputFilename.trim() !== ''
    }
  },
  created () {
    this.fetchUserRecords()
  },
  methods: {
    triggerFileUpload () {
      this.$refs.fileInput.click()
    },
    handleFileSelect (event) {
      const files = event.target.files
      this.addAudios(files)
    },
    handleFileDrop (event) {
      const files = event.dataTransfer.files
      this.addAudios(files)
    },
    addAudios (files) {
      for (const file of files) {
        if (file.type.startsWith('audio/')) {
          this.uploadedAudios.push(file)
        } else {
          alert(`${file.name} 不是有效的音频文件`)
        }
      }
    },
    moveAudioUp (index) {
      if (index > 0) {
        const temp = this.uploadedAudios[index]
        this.$set(this.uploadedAudios, index, this.uploadedAudios[index - 1])
        this.$set(this.uploadedAudios, index - 1, temp)
      }
    },
    moveAudioDown (index) {
      if (index < this.uploadedAudios.length - 1) {
        const temp = this.uploadedAudios[index]
        this.$set(this.uploadedAudios, index, this.uploadedAudios[index + 1])
        this.$set(this.uploadedAudios, index + 1, temp)
      }
    },
    removeAudio (index) {
      if (this.currentAudioIndex === index) {
        this.stopAudio()
      } else if (this.currentAudioIndex > index) {
        this.currentAudioIndex--
      }
      this.uploadedAudios.splice(index, 1)
      this.$delete(this.isPlaying, index)
    },

    async stopAudio () {
      const audioPlayer = this.$refs.audioPlayer
      audioPlayer.pause()
      audioPlayer.currentTime = 0
      if (this.currentAudioIndex !== null) {
        this.$set(this.isPlaying, this.currentAudioIndex, false)
        this.currentAudioIndex = null
      }
      // 添加一个小延迟，确保暂停操作完成
      await new Promise(resolve => setTimeout(resolve, 50))
    },
    playAudio (index) {
      const audioPlayer = this.$refs.audioPlayer
      const audioUrl = URL.createObjectURL(this.uploadedAudios[index])
      audioPlayer.src = audioUrl
      audioPlayer.play()
    },
    async fetchUserRecords () {
      try {
        const data = {
          table_names: this.tableNames
        }
        const response = await getUserRecords(data)
        this.userRecords = response.result
      } catch (error) {
        alert('获取用户记录失败:')
      }
    },
    async deleteRecord (record) {
      try {
        const payload = {
          operation_type: record.operation_type,
          phoneNum: record.phoneNum, // 假设 record 中包含了 phoneNum 字段
          taskId: record.task_id
        }
        const response = await deleteUserRecord(payload)
        // 从本地数组中移除该记录
        if (response.status === 200) {
          alert('删除任务成功')
        } else {
          alert(`删除任务失败: ${response.message}`)
        }
        this.userRecords = this.userRecords.filter(r => r.task_id !== record.task_id)
      } catch (error) {
        alert('删除记录失败:')
      }
    },
    downloadFile (record) {
      if (record.file_status === 'completed' && record.processed_qiniu_url) {
        const link = document.createElement('a')
        link.href = record.processed_qiniu_url
        link.download = record.outputFileName
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        alert('文件处理尚未完成或处理失败,无法下载')
      }
    },
    async startMerging () {
      if (!this.isMergingAllowed) {
        alert('请至少上传两个音频文件并输入输出文件名')
        return
      }

      try {
        const tokenResponse = await getUpQiniuToken()
        const qiniuUploadToken = tokenResponse.uploadToken

        const uploadedFiles = []
        for (const file of this.uploadedAudios) {
          const response = await uploadToQiniu(file, file.name, qiniuUploadToken)

          uploadedFiles.push(`${QINIU_BASE_URL}/${response.data.key}`) // (`http://si78su0ga.hn-bkt.clouddn.com/${response.data.key}`)
          this.file_names.push(response.data.key)
        }

        const data = {
          file_names: this.file_names,
          file_qiniu_urls: uploadedFiles,
          merge_order: this.uploadedAudios,
          outputFileName: this.outputFilename,
          operation_type: 'audioMerge'
        }
        const processResponse = await saveAudioAdjustFileUrl(data)
        alert('音频上传成功，合并已开始')
        alert(processResponse.message)
        this.fetchUserRecords()

        // 清空上传的音频列表和输出文件名
        this.uploadedAudios = []
        this.outputFilename = ''
      } catch (error) {
        alert('抱歉，合并过程中出现错误，请稍后重试')
      }
    },
    async togglePlayPause (index) {
      const audioPlayer = this.$refs.audioPlayer
      try {
        if (this.currentAudioIndex === index) {
          if (audioPlayer.paused) {
            await audioPlayer.play()
            this.$set(this.isPlaying, index, true)
          } else {
            audioPlayer.pause()
            this.$set(this.isPlaying, index, false)
          }
        } else {
          await this.stopAudio()
          this.currentAudioIndex = index
          const audioUrl = URL.createObjectURL(this.uploadedAudios[index])
          audioPlayer.src = audioUrl
          await audioPlayer.play()
          this.$set(this.isPlaying, index, true)
        }
      } catch (error) {
        alert('Error playing audio:')
        // 重置播放状态
        this.$set(this.isPlaying, index, false)
      }
    },
    audioEnded () {
      this.stopAudio()
    }
  }
}
</script>

<style scoped>
.audio-merging {
  font-family: Arial, sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  color: white;
  background-color: #1a1a1a;
  margin-top: -100px;
}

h2 {
  text-align: center;
  margin-bottom: 20px;
}

.content-area {
  display: flex;
  gap: 20px;
}

.left-area {
  width: 450px;
  height: 300px;
}

.right-area {
  width: 350px;
  height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.audio-list {
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background-color: #2a2a2a;
  border-radius: 8px;
  padding: 10px;
}

.upload-audio {
  width: 100%;
  height: 240px;
  background-color: #2a2a2a;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-bottom: 10px;
}

.upload-icon {
  font-size: 24px;
  color: #00bcd4;
}

.audio-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.audio-name {
  width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.audio-controls {
  width: 230px;
  display: flex;
  justify-content: flex-end;
}

.audio-item button {
  background-color: #00bcd4;
  color: white;
  border: none;
  padding: 2px 5px;
  border-radius: 4px;
  cursor: pointer;
  margin-left: 5px;
}

.output-filename {
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.output-filename label {
  margin-right: 10px;
}

.output-filename input {
  flex-grow: 1;
  padding: 5px;
  background-color: #333;
  color: white;
  border: 1px solid #555;
  border-radius: 4px;
}

.process-button {
  width: 100%;
  padding: 10px;
  background-color: #00bcd4;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.process-button:disabled {
  background-color: #555;
  cursor: not-allowed;
}

.terms {
  font-size: 12px;
  color: #888;
  margin-top: 20px;
}

.user-records {
  margin-top: 20px;
  background-color: #2a2a2a;
  padding: 10px;
  border-radius: 8px;
}
.user-records {
  height: 150px;
  overflow-y: auto;
}
.user-records h3 {
  color: white;
  margin-bottom: 15px;
  text-align: center;
}

.table-container {
  height: 150px;
  overflow-y: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  color: white;
}

th, td {
  border: 1px solid #444;
  padding: 5px;
  text-align: left;
}

th {
  background-color: #333;
}

tr:nth-child(even) {
  background-color: #3a3a3a;
}

a {
  color: #00bcd4;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

.user-records button {
  background-color: #f44336;
  color: white;
  border: none;
  padding: 5px 10px;
  border-radius: 4px;
  cursor: pointer;
}

.user-records button:hover {
  background-color: #d32f2f;
}

@media (max-width: 1200px) {
  .content-area {
    flex-direction: column;
  }

  .left-area, .right-area {
    width: 100%;
    height: auto;
  }

  .audio-list {
    height: 300px;
  }

  .right-area {
    margin-top: 20px;
  }
}
.play-pause-btn {
  background: none;
  border: none;
  color: #00bcd4;
  font-size: 16px;
  cursor: pointer;
  padding: 5px;
}

.play-pause-btn:focus {
  outline: none;
}
</style>
